import React, { useState } from 'react';
import {
  Menu,
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  List,
  Tooltip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { NotificationItem } from './NotificationItem';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification, NotificationType } from '../../types/workflow';

interface NotificationDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

/**
 * NotificationDropdown Component
 *
 * A MUI Menu/Popover that displays recent notifications with:
 * - Header with title and "Mark all as read" button
 * - List of NotificationItem components (max 10 recent from context)
 * - Loading state with CircularProgress
 * - Empty state when no notifications
 * - "View all notifications" footer link -> /notifications
 */
export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const {
    recentNotifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
  } = useNotifications();
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Refresh recent notifications when dropdown opens
  const handleEntered = () => {
    refreshNotifications();
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.isRead) {
        await markAsRead(notification.id);
      }
      onClose();
      if (notification.entityType === 'BatchWorkflow' && notification.entityId) {
        navigate(`/workflow/${notification.entityId}`);
      } else if (notification.type === NotificationType.NormUpgradeAvailable) {
        navigate('/hybrid-norms-view');
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to mark notification as read';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      await markAllAsRead();
      enqueueSnackbar('All notifications marked as read', { variant: 'success' });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to mark all notifications as read';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  const hasUnreadNotifications = recentNotifications.some((n) => !n.isRead);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxHeight: 480,
            overflow: 'hidden',
          },
        },
        list: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: 480,
            overflow: 'hidden',
            py: 0,
          },
        },
      }}
      TransitionProps={{
        onEntered: handleEntered,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Notifications
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasUnreadNotifications && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              disabled={markingAllRead}
              sx={{
                fontSize: '0.75rem',
                textTransform: 'none',
              }}
            >
              {markingAllRead ? 'Marking...' : 'Mark all as read'}
            </Button>
          )}
          <Tooltip title="Notification settings">
            <IconButton
              size="small"
              onClick={() => { onClose(); navigate('/notifications'); }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 100,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : recentNotifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              px: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              No notifications
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              align="center"
              sx={{ mt: 0.5 }}
            >
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {recentNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Footer - sticky at bottom */}
      <Divider sx={{ flexShrink: 0 }} />
      <Box
        sx={{
          px: 2,
          py: 1,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          fullWidth
          sx={{
            textTransform: 'none',
            color: 'primary.main',
          }}
          onClick={handleViewAll}
        >
          View all notifications
        </Button>
      </Box>
    </Menu>
  );
};

export default NotificationDropdown;
